import { conversationApiClient } from '@consentropy/shared';
import { API_PATHS } from '@consentropy/shared';
import { mapTimestamps } from '@consentropy/shared';
import type { ApiListResponse, ApiItemResponse } from '@consentropy/shared';
import type {
  Conversation,
  CreateConversationInput,
  UpdateConversationInput,
} from '@consentropy/shared';
import { createMockStore, nowISO } from './mockHelpers';

const USE_MOCK = import.meta.env.VITE_PHASE3_USE_MOCK_CONVERSATIONS === 'true';
const MOCK_TRANSCRIPTS_STORAGE_KEY = 'phase3_transcripts';

export type CreateChildConversationInput =
  Omit<CreateConversationInput, 'childOfConversationId' | 'parentConversationId'> & {
    childOfConversationId: string;
    copyTranscriptsFromParent: true;
  };

export interface CreateChildConversationResult {
  conversation: Conversation;
  copiedTranscriptCount: number;
}

// Real implementations

type BackendConversation = Omit<Conversation, 'createdAt' | 'updatedAt'> & {
  created: string;
  lastModified: string;
};

function mapConversation(item: BackendConversation): Conversation {
  const conversation = mapTimestamps(item) as unknown as Conversation;
  return {
    ...conversation,
    childOfConversationId: conversation.childOfConversationId ?? conversation.parentConversationId,
  };
}

async function realGetConversations(): Promise<Conversation[]> {
  const res = await conversationApiClient.get<ApiListResponse<BackendConversation>>(
    API_PATHS.conversations,
  );
  return res.items.map(mapConversation);
}

async function realGetConversationsByProject(projectId: string): Promise<Conversation[]> {
  const res = await conversationApiClient.get<ApiListResponse<BackendConversation>>(
    `${API_PATHS.conversations}?projectId=${encodeURIComponent(projectId)}`,
  );
  return res.items.map(mapConversation);
}

async function realGetConversationById(id: string): Promise<Conversation> {
  const res = await conversationApiClient.get<ApiItemResponse<BackendConversation>>(
    `${API_PATHS.conversations}${id}`,
  );
  return mapConversation(res.item);
}

async function realCreateConversation(input: CreateConversationInput): Promise<Conversation> {
  const res = await conversationApiClient.post<ApiItemResponse<BackendConversation>>(
    API_PATHS.conversations,
    input,
  );
  return mapConversation(res.item);
}

async function realCreateChildConversation(
  input: CreateChildConversationInput,
): Promise<CreateChildConversationResult> {
  const res = await conversationApiClient.post<
    ApiItemResponse<BackendConversation> & { copiedTranscriptCount?: number }
  >(API_PATHS.conversations, input);
  return {
    conversation: mapConversation(res.item),
    copiedTranscriptCount: res.copiedTranscriptCount ?? 0,
  };
}

async function realUpdateConversation(id: string, input: UpdateConversationInput): Promise<Conversation> {
  const res = await conversationApiClient.patch<ApiItemResponse<BackendConversation>>(
    `${API_PATHS.conversations}${id}`,
    input,
  );
  return mapConversation(res.item);
}

async function realDeleteConversation(id: string): Promise<void> {
  await conversationApiClient.delete(`${API_PATHS.conversations}${id}`);
}

async function realGetChildConversations(parentId: string): Promise<Conversation[]> {
  const all = await realGetConversations();
  return all.filter((c) => c.childOfConversationId === parentId);
}

// Mock implementations

const mockStore = createMockStore<Conversation>('phase3_conversations');

async function mockGetConversations(): Promise<Conversation[]> {
  return mockStore.getAll();
}

async function mockGetConversationsByProject(projectId: string): Promise<Conversation[]> {
  return mockStore.getAll().filter((c) => c.projectId === projectId);
}

async function mockGetConversationById(id: string): Promise<Conversation> {
  const item = mockStore.getById(id);
  if (!item) throw new Error(`Conversation ${id} not found`);
  return item;
}

async function mockCreateConversation(input: CreateConversationInput): Promise<Conversation> {
  const now = nowISO();
  return mockStore.create({ ...input, createdAt: now, updatedAt: now, createdBy: 'mock-user' } as Conversation);
}

function copyMockTranscripts(parentConversationId: string, childConversationId: string): number {
  try {
    const raw = localStorage.getItem(MOCK_TRANSCRIPTS_STORAGE_KEY);
    const all = raw ? JSON.parse(raw) as Record<string, Array<Record<string, unknown>>> : {};
    const parentTranscripts = all[parentConversationId] ?? [];
    const copiedAt = nowISO();

    all[childConversationId] = parentTranscripts.map((transcript, index) => ({
      ...transcript,
      transcriptId: `transcript-${childConversationId}-${Date.now()}-${index}`,
      conversationId: childConversationId,
      createdAt: copiedAt,
      updatedAt: copiedAt,
    }));
    localStorage.setItem(MOCK_TRANSCRIPTS_STORAGE_KEY, JSON.stringify(all));
    return parentTranscripts.length;
  } catch {
    return 0;
  }
}

async function mockCreateChildConversation(
  input: CreateChildConversationInput,
): Promise<CreateChildConversationResult> {
  const { copyTranscriptsFromParent, ...conversationInput } = input;
  void copyTranscriptsFromParent;
  const conversation = await mockCreateConversation(conversationInput);
  return {
    conversation,
    copiedTranscriptCount: copyMockTranscripts(input.childOfConversationId, conversation.id),
  };
}

async function mockUpdateConversation(id: string, input: UpdateConversationInput): Promise<Conversation> {
  return mockStore.update(id, { ...input, updatedAt: nowISO() });
}

async function mockDeleteConversation(id: string): Promise<void> {
  mockStore.remove(id);
}

async function mockGetChildConversations(parentId: string): Promise<Conversation[]> {
  return mockStore.getAll().filter((c) => c.childOfConversationId === parentId);
}

// Exports

export const getConversations = USE_MOCK ? mockGetConversations : realGetConversations;
export const getConversationsByProject = USE_MOCK ? mockGetConversationsByProject : realGetConversationsByProject;
export const getConversationById = USE_MOCK ? mockGetConversationById : realGetConversationById;
export const createConversation = USE_MOCK ? mockCreateConversation : realCreateConversation;
export const createChildConversation = USE_MOCK
  ? mockCreateChildConversation
  : realCreateChildConversation;
export const updateConversation = USE_MOCK ? mockUpdateConversation : realUpdateConversation;
export const deleteConversation = USE_MOCK ? mockDeleteConversation : realDeleteConversation;
export const getChildConversations = USE_MOCK ? mockGetChildConversations : realGetChildConversations;
